import { z } from 'zod'

export const LEARNING_RECORDS_READ_CONTRACT_VERSION = '1.0.0' as const

const nullableString = z.string().nullable()
const jsonObject = z.record(z.unknown())

const artifactSchema = z.object({
  id: z.string().min(1),
  catalogNodeId: z.string().min(1),
  version: z.string().min(1),
  storageUri: z.string().min(1),
  sha256: z.string().min(1),
  hashVerificationStatus: z.enum(['pending', 'verified', 'failed']),
  hashVerifiedAt: nullableString.optional(),
  hashVerifiedBy: nullableString.optional(),
  hashVerifiedSha256: nullableString.optional(),
  manifest: jsonObject,
  releaseChannel: z.enum(['draft', 'preview', 'production', 'archived']),
  admissionStatus: z.enum(['pending', 'accepted', 'rejected', 'revoked']),
  publishedAt: nullableString.optional(),
  publishedBy: nullableString.optional(),
  sourceSystemId: nullableString.optional(),
  sourceRef: nullableString.optional(),
  createdBy: nullableString.optional(),
  updatedBy: nullableString.optional(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const exportPackageSchema = artifactSchema.extend({
  sourceArtifactId: nullableString,
  packageFamily: z.enum(['scorm', 'common_cartridge', 'cmi5', 'standalone_spa', 'lms_specific']),
  packageSchemaVersion: z.string().min(1),
}).passthrough()

const sourceSystemSchema = z.object({
  id: z.string().min(1),
  kind: z.string().min(1),
  displayName: z.string().min(1),
  status: z.enum(['active', 'disabled', 'draft']),
  syncDirection: z.enum(['pull', 'push', 'bidirectional', 'manual', 'none']),
  secretRef: nullableString,
  configJson: jsonObject,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const grantRuleSchema = z.object({
  id: z.string().min(1),
  membershipKey: z.string().min(1),
  programKey: nullableString,
  catalogNodeId: z.string().min(1),
  grantPolicy: z.enum(['grant_node_only', 'grant_descendants', 'grant_bundle_courses', 'manual_review']),
  status: z.enum(['draft', 'active', 'inactive']),
  effectiveFrom: nullableString,
  effectiveTo: nullableString,
  createdBy: nullableString,
  updatedBy: nullableString,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const reconciliationSchema = z.object({
  id: z.string().min(1),
  memberProfileId: z.string().min(1),
  catalogNodeId: z.string().min(1),
  entitlementGrantId: nullableString,
  expectedSourceRef: z.string().min(1),
  externalEnrollmentId: nullableString,
  observedSourceSystemId: z.string().min(1),
  observedSourceKind: z.enum([
    'thinkific_enrollment',
    'coaching_participation',
    'lmengine_projection',
    'artifact_delivery',
    'export_package_delivery',
  ]),
  state: z.enum([
    'expected_and_present',
    'expected_missing_external',
    'external_without_active_grant',
    'unmapped_external_asset',
    'identity_ambiguous',
    'source_error',
    'unknown',
  ]),
  severity: z.enum(['info', 'warn', 'blocker']),
  recommendedAction: nullableString,
  status: z.enum(['open', 'acknowledged', 'resolved', 'quarantined']),
  assignedTo: nullableString,
  assignedBy: nullableString,
  assignedAt: nullableString,
  assignmentReason: nullableString,
  acknowledgedBy: nullableString,
  acknowledgedAt: nullableString,
  acknowledgeReason: nullableString,
  resolution: nullableString,
  resolutionReason: nullableString,
  resolvedBy: nullableString,
  resolvedAt: nullableString,
  linkedMappingId: nullableString,
  quarantineReason: nullableString,
  quarantinedBy: nullableString,
  quarantinedAt: nullableString,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const thinkificAssignmentSchema = z.object({
  id: z.string().min(1),
  importRunId: z.string().min(1),
  sourceSystemId: z.string().min(1),
  idempotencyKey: z.string().min(1),
  assignmentOrder: z.number().int().positive(),
  membership: z.string().min(1),
  thinkificProgramName: z.string().min(1),
  thinkificType: z.enum(['Course', 'Bundle']),
  thinkificId: z.string().min(1),
  externalEntityId: nullableString,
  mappingStatus: z.enum(['unmapped', 'mapped', 'ambiguous', 'rejected']),
  catalogMappingId: nullableString,
  rawPayloadHash: z.string().min(1),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const thinkificEnrollmentSchema = z.object({
  id: z.string().min(1),
  sourceSystemId: z.literal('thinkific'),
  externalEnrollmentId: z.string().min(1),
  externalEntityId: z.string().min(1),
  memberProfileId: nullableString,
  externalMemberId: nullableString,
  email: z.string().email(),
  progressDecimal: z.number().min(0).max(1).nullable(),
  progressPercent: z.number().min(0).max(100).nullable(),
  status: z.enum(['not_started', 'in_progress', 'completed', 'missing_in_source', 'unknown']),
  completedAt: nullableString,
  rawPayloadHash: z.string().min(1),
  syncedAt: z.string().min(1),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const listEnvelope = z.object({
  contract_version: z.literal(LEARNING_RECORDS_READ_CONTRACT_VERSION),
  page: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  nextCursor: z.string().nullable(),
}).passthrough()

export const learningArtifactListResponseSchema = listEnvelope.extend({
  items: z.array(artifactSchema),
}).passthrough()
export const learningArtifactDetailResponseSchema = z.object({
  contract_version: z.literal(LEARNING_RECORDS_READ_CONTRACT_VERSION),
  item: artifactSchema,
}).passthrough()
export const learningExportPackageListResponseSchema = listEnvelope.extend({
  items: z.array(exportPackageSchema),
}).passthrough()
export const learningExportPackageDetailResponseSchema = z.object({
  contract_version: z.literal(LEARNING_RECORDS_READ_CONTRACT_VERSION),
  item: exportPackageSchema,
}).passthrough()
export const learningGrantRuleListResponseSchema = listEnvelope.extend({
  items: z.array(grantRuleSchema),
}).passthrough()
export const learningReconciliationListResponseSchema = listEnvelope.extend({
  items: z.array(reconciliationSchema),
}).passthrough()
export const learningReconciliationDetailResponseSchema = z.object({
  contract_version: z.literal(LEARNING_RECORDS_READ_CONTRACT_VERSION),
  item: reconciliationSchema,
}).passthrough()
export const learningSourceSystemListResponseSchema = listEnvelope.extend({
  items: z.array(sourceSystemSchema),
}).passthrough()
export const learningSourceSystemDetailResponseSchema = z.object({
  contract_version: z.literal(LEARNING_RECORDS_READ_CONTRACT_VERSION),
  item: sourceSystemSchema,
}).passthrough()
export const learningThinkificAssignmentListResponseSchema = listEnvelope.extend({
  items: z.array(thinkificAssignmentSchema),
}).passthrough()
export const learningThinkificEnrollmentListResponseSchema = listEnvelope.extend({
  items: z.array(thinkificEnrollmentSchema),
}).passthrough()

export type LearningArtifact = z.infer<typeof artifactSchema>
export type LearningArtifactListResponse = z.infer<typeof learningArtifactListResponseSchema>
export type LearningArtifactDetailResponse = z.infer<typeof learningArtifactDetailResponseSchema>
export type LearningExportPackage = z.infer<typeof exportPackageSchema>
export type LearningExportPackageListResponse = z.infer<typeof learningExportPackageListResponseSchema>
export type LearningExportPackageDetailResponse = z.infer<typeof learningExportPackageDetailResponseSchema>
export type LearningGrantRule = z.infer<typeof grantRuleSchema>
export type LearningGrantRuleListResponse = z.infer<typeof learningGrantRuleListResponseSchema>
export type LearningReconciliation = z.infer<typeof reconciliationSchema>
export type LearningReconciliationListResponse = z.infer<typeof learningReconciliationListResponseSchema>
export type LearningReconciliationDetailResponse = z.infer<typeof learningReconciliationDetailResponseSchema>
export type LearningSourceSystem = z.infer<typeof sourceSystemSchema>
export type LearningSourceSystemListResponse = z.infer<typeof learningSourceSystemListResponseSchema>
export type LearningSourceSystemDetailResponse = z.infer<typeof learningSourceSystemDetailResponseSchema>
export type LearningThinkificAssignment = z.infer<typeof thinkificAssignmentSchema>
export type LearningThinkificAssignmentListResponse = z.infer<typeof learningThinkificAssignmentListResponseSchema>
export type LearningThinkificEnrollment = z.infer<typeof thinkificEnrollmentSchema>
export type LearningThinkificEnrollmentListResponse = z.infer<typeof learningThinkificEnrollmentListResponseSchema>
